import { useContext, useEffect, useState } from "react";
import {
  IonItem,
  IonLabel,
  IonNote,
  IonSelect,
  IonSelectOption,
} from "@ionic/react";
import { TranslationsContext } from "../util/Translations";
import { useSettingsStore } from "../store/settings";
import { getSyCode30Records } from "../api/SyCodes";

interface PhoneCodeOption {
  Code: string;
  Description?: string;
}

/**
 * Device-local setting: which Phone Code validation values
 * (em_employee_phone.phone_code) are text-message capable. Numbers with a
 * selected code get a Text (SMS) action in the employee contact actions.
 *
 * There is no reliable "mobile" code in EAM (Phone Codes are customer
 * configurable — e.g. WORK/HOME/BUSINESS), so the textable codes are chosen
 * here rather than inferred.
 */
const SmsPhoneCodesSetting: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const smsCapablePhoneCodes = useSettingsStore(
    (state) => state.smsCapablePhoneCodes
  );
  const setSmsCapablePhoneCodes = useSettingsStore(
    (state) => state.setSmsCapablePhoneCodes
  );
  const [options, setOptions] = useState<PhoneCodeOption[]>([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // Phone Code is a sy_code_30 validation subcategory (Business/HOME/WORK…).
        const response = await getSyCode30Records({
          subCategory: "Phone Code",
          isDataTransNeeded: true,
        });
        if (!active) return;

        // The response body can arrive in a few shapes depending on the
        // endpoint/data-translation flag: a bare array, an object with
        // Code30UdValues, or a JSON string. Resolve them all to a row array.
        let data: any = response?.data;
        if (typeof data === "string") {
          try {
            data = JSON.parse(data);
          } catch {
            data = [];
          }
        }
        const rows: any[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.Code30UdValues)
          ? data.Code30UdValues
          : Array.isArray(data?.data)
          ? data.data
          : [];

        // De-dupe by Code (the response can repeat codes across translations).
        const seen = new Set<string>();
        const opts: PhoneCodeOption[] = [];
        rows.forEach((c: any) => {
          const code = c?.Code ?? c?.code;
          if (code && !seen.has(code)) {
            seen.add(code);
            opts.push({ Code: code, Description: c?.Description ?? c?.description });
          }
        });
        setOptions(opts);
      } catch {
        // Non-fatal: if codes can't be loaded, the select just shows nothing.
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <IonItem>
      <IonLabel position="stacked">
        {translations["lbl_sms_phone_types"] || "Text-enabled Phone Types"}
      </IonLabel>
      <IonSelect
        multiple
        value={smsCapablePhoneCodes ?? []}
        placeholder={translations["lbl_btn_select"] || "Select"}
        onIonChange={(e) => setSmsCapablePhoneCodes(e.detail.value ?? [])}
      >
        {options.map((opt) => (
          <IonSelectOption key={opt.Code} value={opt.Code}>
            {opt.Description ? `${opt.Code} - ${opt.Description}` : opt.Code}
          </IonSelectOption>
        ))}
      </IonSelect>
      <IonNote slot="helper">
        {translations["lbl_sms_phone_types_help"] ||
          "Phone types that can receive text messages. Numbers with these types show a Text action."}
      </IonNote>
    </IonItem>
  );
};

export default SmsPhoneCodesSetting;
